import type { CSSProperties } from 'react';
import type { DepartmentId } from './StartProductionDialog';

function FilmArt() {
  return (
    <svg viewBox="0 0 320 132" aria-hidden="true">
      <g className="da-page">
        <rect className="da-frame" x="16" y="14" width="62" height="84" rx="4" />
        <text className="da-label" x="24" y="30">INT. DAY</text>
        {[40, 50, 60, 70, 80].map((y, index) => (
          <rect key={y} className="da-ink da-script" x={index % 2 ? 32 : 24} y={y} width={index % 2 ? 36 : 46} height="3" rx="1.5" style={{ '--d': index } as CSSProperties} />
        ))}
      </g>
      <path className="da-wire" d="M84 56h18" />
      <g className="da-boards">
        {[0, 1, 2].map((index) => (
          <g key={index} className={`da-board da-board-${index}`}>
            <rect className={index === 1 ? 'da-frame da-frame--hero' : 'da-frame'} x={108 + index * 64} y="18" width="56" height="36" rx="3" />
            <text className="da-label" x={112 + index * 64} y="64">SH {String(index + 1).padStart(2, '0')}</text>
          </g>
        ))}
        <path className="da-ink-stroke da-shot-0" d="M118 46l10-12 8 8 6-5 10 9" />
        <circle className="da-ink-fill da-shot-1" cx="200" cy="34" r="8" />
        <rect className="da-ink-fill da-shot-2" x="252" y="28" width="16" height="18" rx="3" />
      </g>
      <rect className="da-track" x="108" y="84" width="184" height="10" rx="2" />
      {[0, 1, 2].map((index) => (
        <rect key={index} className={`da-clip da-clip-${index}`} x={110 + index * 61} y="86" width="58" height="6" rx="1.5" />
      ))}
      <g className="da-playhead">
        <rect className="da-accent" x="107" y="76" width="2" height="24" rx="1" />
        <path className="da-accent" d="M104 74h8l-4 5z" />
      </g>
      <text className="da-label da-tc" x="108" y="116">00:00:00:00</text>
      <text className="da-label da-tc da-tc-end" x="108" y="116">00:01:24:12</text>
    </svg>
  );
}

function MarketingArt() {
  return (
    <svg viewBox="0 0 320 132" aria-hidden="true">
      <g className="da-mk da-mk-story">
        <rect className="da-frame" x="40" y="26" width="46" height="82" rx="4" />
        <rect className="da-ink" x="48" y="92" width="30" height="3" rx="1.5" />
        <text className="da-label" x="40" y="122">9:16</text>
      </g>
      <g className="da-mk da-mk-square">
        <rect className="da-frame" x="232" y="60" width="52" height="52" rx="4" />
        <rect className="da-ink" x="239" y="100" width="24" height="3" rx="1.5" />
        <text className="da-label" x="290" y="90">1:1</text>
      </g>
      <g className="da-mk da-mk-wide">
        <rect className="da-frame" x="214" y="10" width="72" height="40" rx="4" />
        <text className="da-label" x="290" y="34">16:9</text>
      </g>
      <path className="da-wire da-mk-link da-mk-link-a" d="M140 66C112 66 104 62 88 62" pathLength={1} />
      <path className="da-wire da-mk-link da-mk-link-b" d="M180 72C200 72 212 84 232 84" pathLength={1} />
      <path className="da-wire da-mk-link da-mk-link-c" d="M176 52C194 44 200 30 214 30" pathLength={1} />
      <g className="da-product">
        <circle className="da-ring" cx="160" cy="66" r="40" />
        <rect className="da-frame da-frame--hero" x="146" y="44" width="28" height="48" rx="7" />
        <rect className="da-accent" x="152" y="36" width="16" height="10" rx="2" />
        <rect className="da-ink" x="152" y="62" width="16" height="10" rx="1.5" />
      </g>
      <g className="da-lock">
        <rect className="da-lock-body" x="172" y="86" width="16" height="12" rx="2" />
        <path className="da-lock-shackle" d="M175 86v-3a5 5 0 0 1 10 0v3" />
      </g>
    </svg>
  );
}

function VfxArt() {
  return (
    <svg viewBox="0 0 64 48" aria-hidden="true">
      <rect className="da-frame" x="4" y="4" width="56" height="40" rx="3" />
      <path className="da-matte" d="M22 40c0-12 4-22 12-22s10 10 10 22z" />
      <g className="da-track-mark da-tm-a"><path className="da-accent-stroke" d="M14 12h6M17 9v6" /></g>
      <g className="da-track-mark da-tm-b"><path className="da-accent-stroke" d="M46 30h6M49 27v6" /></g>
    </svg>
  );
}

function SaasArt() {
  return (
    <svg viewBox="0 0 64 48" aria-hidden="true">
      <rect className="da-frame" x="4" y="6" width="56" height="36" rx="3" />
      <path className="da-wire" d="M4 13h56" />
      <circle className="da-ink-fill" cx="9" cy="9.5" r="1.3" />
      <rect className="da-ink" x="10" y="19" width="22" height="3" rx="1.5" />
      <rect className="da-ink" x="10" y="25" width="16" height="3" rx="1.5" />
      <rect className="da-saas-btn" x="38" y="30" width="16" height="7" rx="2" />
      <path className="da-cursor" d="M26 30v11l3-3 2 4 2-1-2-4h4z" />
    </svg>
  );
}

function TalkArt() {
  return (
    <svg viewBox="0 0 64 48" aria-hidden="true">
      <path className="da-frame" d="M8 8h36a4 4 0 0 1 4 4v14a4 4 0 0 1-4 4H22l-8 7v-7H8a4 4 0 0 1-4-4V12a4 4 0 0 1 4-4z" />
      {[0, 1, 2].map((dot) => <circle key={dot} className="da-ink-fill da-typing" cx={18 + dot * 8} cy="19" r="2" style={{ '--d': dot } as CSSProperties} />)}
      {[0, 1, 2].map((row) => (
        <g key={row} className="da-plan-row" style={{ '--d': row } as CSSProperties}>
          <rect className="da-plan-check" x="36" y={20 + row * 8} width="5" height="5" rx="1" />
          <rect className="da-ink" x="44" y={21 + row * 8} width={row === 1 ? 10 : 16} height="3" rx="1.5" />
        </g>
      ))}
    </svg>
  );
}

function ViralArt() {
  return (
    <svg viewBox="0 0 64 48" aria-hidden="true">
      <defs>
        <clipPath id="da-vb-hero-clip"><rect x="4" y="4" width="26" height="40" rx="3" /></clipPath>
      </defs>
      <g className="da-vb-tile da-vb-tile-0">
        <rect className="da-frame da-vb-hero" x="4" y="4" width="26" height="40" rx="3" />
        <g clipPath="url(#da-vb-hero-clip)">
          <rect className="da-vb-glow" x="4" y="4" width="26" height="40" />
          <rect className="da-vb-sweep" x="1" y="4" width="2" height="40" />
        </g>
        <path className="da-ink-fill da-vb-play" d="M13 18l10 6-10 6z" />
        <text className="da-label da-vb-count" x="17" y="39" textAnchor="middle">2.4M</text>
      </g>
      <g className="da-vb-tile da-vb-tile-1">
        <rect className="da-frame" x="34" y="4" width="26" height="18" rx="3" />
        <path className="da-vb-heart" d="M41 11.2c-1-1.4-3.2-.8-3.2 1 0 1.5 1.6 2.6 3.2 3.8 1.6-1.2 3.2-2.3 3.2-3.8 0-1.8-2.2-2.4-3.2-1z" />
        <rect className="da-ink da-vb-bar" x="47" y="12" width="9" height="3" rx="1.5" />
      </g>
      <g className="da-vb-tile da-vb-tile-2">
        <rect className="da-frame" x="34" y="26" width="26" height="18" rx="3" />
        <path className="da-vb-trend" d="M38 39l5-4 4 2.5 6-6" />
        <path className="da-vb-trend-head" d="M50 31.5h3.2v3.2" />
      </g>
    </svg>
  );
}

function UpscaleArt() {
  return (
    <svg viewBox="0 0 64 48" aria-hidden="true">
      <rect className="da-frame" x="4" y="4" width="56" height="40" rx="3" />
      <g className="da-pixels">
        {[[24, 12], [32, 12], [16, 20], [24, 20], [32, 20], [40, 20], [24, 28], [32, 28]].map(([x, y]) => (
          <rect key={`${x}-${y}`} className="da-ink-fill" x={x} y={y} width="8" height="8" />
        ))}
      </g>
      <circle className="da-smooth" cx="32" cy="24" r="13" />
      <rect className="da-scan" x="4" y="4" width="2" height="40" />
      <text className="da-label da-res" x="40" y="41">4K</text>
    </svg>
  );
}

export default function DepartmentArt({ id }: { id: DepartmentId }) {
  if (id === 'film') return <FilmArt />;
  if (id === 'marketing') return <MarketingArt />;
  if (id === 'vfx') return <VfxArt />;
  if (id === 'saas') return <SaasArt />;
  if (id === 'talk') return <TalkArt />;
  if (id === 'upscale') return <UpscaleArt />;
  if (id === 'viral') return <ViralArt />;
  return null;
}
